/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import styled from '@emotion/styled'
import Button from 'react-bootstrap/Button'
import moment from 'moment'
import { Link } from 'react-router-dom'

import { TableWrapper } from 'components/atoms/TableWrapper'
import DataTable from 'components/atoms/DataTable'
import { confirm } from 'components/molecules/Confirmation'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import { useSendFaxMutation } from 'store/services/labOrder'
import { useGetOutgoingDocumentsQuery } from 'store/services/outgoingDocuments'
import { OutgoingDocumentsStatus } from 'store/types'
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const StyledTableWrapper = styled(TableWrapper)`
  thead {
    z-index: 1;
  }
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const statusVariant = (status?: string | null) => {
  if (status === 'FAILED') return 'danger'
  if (status === 'SENT') return 'success'
  if (status === 'PENDING') return 'warning'
  return 'secondary'
}

// The work queue is what the menu badge counts; Sent is the record of what
// went out, a DME referral packet among it (TES-3159).
const STATUS_FILTERS: { value: OutgoingDocumentsStatus; label: string }[] = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'sent', label: 'Sent' },
  { value: 'all', label: 'All' },
]

const OutgoingDocuments = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [status, setStatus] = useState<OutgoingDocumentsStatus>('attention')
  const [sendFax, { isLoading: isRetrying }] = useSendFaxMutation()

  const { data, isLoading } = useGetOutgoingDocumentsQuery(
    { page: currentPage, size: pageSize, status },
    {
      pollingInterval: 30000,
      refetchOnFocus: true,
    },
  )

  const header = [
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const disabled = row.original.faxStatus !== 'FAILED' || !row.original.faxedTo || isRetrying

        return (
          <Button
            style={{ borderRadius: '50%' }}
            variant="outline-primary"
            size="sm"
            disabled={disabled}
            title={row.original.faxError || undefined}
            onClick={() =>
              confirm('', {
                title: 'Retry faxing?',
                description: 'This will queue this file to be faxed again.',
                yesAction: 'Retry',
                noAction: 'Cancel',
              }).then(() =>
                sendFax({
                  orderId: row.original.orderId,
                  documentKeys: [row.original.fileKey],
                  faxNumber: row.original.faxedTo,
                  message: '',
                }).unwrap(),
              )
            }>
            <FontAwesomeIcon icon={faRedo} />
          </Button>
        )
      },
    },
    {
      Header: 'Status',
      accessor: 'faxStatus',
      Cell: ({ row }) => (
        <span className={`badge badge-${statusVariant(row.original.faxStatus)}`}>
          {row.original.faxStatus || '-'}
        </span>
      ),
    },
    {
      Header: 'Order ID',
      accessor: 'orderId',
      Cell: ({ row }) => (
        <Link
          to={`/lab/orderEdit/${row.original.orderId}`}
          className="border border-primary rounded"
          css={{ padding: '0.45rem 0.65rem' }}>
          #{row.original.orderId}
        </Link>
      ),
    },
    {
      Header: 'Document Name',
      accessor: 'fileName',
      className: 'col-2',
      Cell: ({ row }) => (
        <>
          {row.original.previewUrl ? (
            <a href={row.original.previewUrl} target="_blank" rel="noreferrer">
              {row.original.fileName || '-'}
            </a>
          ) : (
            <span>{row.original.fileName || '-'}</span>
          )}
          {!!row.original.contents?.length && (
            <div className="text-muted" css={{ fontSize: '0.8rem', marginTop: 2 }}>
              Included: {row.original.contents.join(', ')}
            </div>
          )}
        </>
      ),
    },
    {
      Header: 'Document Type',
      accessor: 'uploadType',
      className: 'col-1',
      Cell: ({ row }) => <span>{row.original.uploadType || '-'}</span>,
    },
    {
      Header: 'Fax To',
      accessor: 'faxedTo',
      className: 'col-2',
      Cell: ({ row }) => (
        <>
          {row.original.recipientName && (
            <div css={{ fontWeight: 600 }}>{row.original.recipientName}</div>
          )}
          <span>{row.original.faxedTo || '-'}</span>
        </>
      ),
    },
    {
      Header: 'Last Fax Attempt',
      accessor: 'lastFaxAttempt',
      className: 'col-2',
      Cell: ({ row }) => (
        <span>{row.original.lastFaxAttempt ? moment(row.original.lastFaxAttempt).format('lll') : '-'}</span>
      ),
    },
    {
      Header: 'Fax Error',
      accessor: 'faxError',
      className: 'col-2',
      Cell: ({ row }) => <span>{row.original.faxError || '-'}</span>,
    },
  ]

  return (
    <DashboardContent
      title="Outgoing Documents"
      icon="fa fa-arrow-up"
      content={
        <div className="card card-primary mt-3">
          <div className="card-body row align-items-center">
            <div className="col-12 d-flex justify-content-end">
              <div className="btn-group btn-group-sm" role="group" aria-label="Fax status">
                {STATUS_FILTERS.map((filter) => (
                  <Button
                    key={filter.value}
                    variant={status === filter.value ? 'primary' : 'outline-primary'}
                    aria-pressed={status === filter.value}
                    onClick={() => {
                      setStatus(filter.value)
                      setCurrentPage(0)
                    }}>
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>
            <StyledTableWrapper className="card-body row">
              <DataTable
                tableKey="outgoing_documents"
                data={data}
                status={isLoading}
                tableColumns={header}
                entityName="outgoing documents"
                pageSize={pageSize}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                setSortOptions={() => {}}
                searchable={false}
              />
            </StyledTableWrapper>
          </div>
        </div>
      }
      breadcrumb={[
        {
          label: 'Outgoing Documents',
        },
      ]}
    />
  )
}

export default OutgoingDocuments
